import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaPython, FaNodeJs, FaDatabase, FaChartLine, FaRobot, FaCloud, FaFilePdf } from 'react-icons/fa';
import type { IconType } from 'react-icons';

interface Feature {
  icon: IconType;
  iconClass: string;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: FaPython,
    iconClass: 'text-blue-600',
    title: "Python Scraping",
    description: "Advanced web scraping solutions using Python with BeautifulSoup, Selenium, and Scrapy for complex data extraction."
  },
  {
    icon: FaNodeJs,
    iconClass: 'text-green-600',
    title: "Node.js Automation",
    description: "Efficient data collection using Node.js with Puppeteer and Cheerio for dynamic web content extraction."
  },
  {
    icon: FaDatabase,
    iconClass: 'text-purple-600',
    title: "Data Processing",
    description: "Intelligent data structuring and cleaning to ensure high-quality, actionable insights."
  },
  {
    icon: FaChartLine,
    iconClass: 'text-red-600',
    title: "Market Analysis",
    description: "Comprehensive market research and competitive analysis through automated data collection."
  },
  {
    icon: FaRobot,
    iconClass: 'text-yellow-600',
    title: "AI Integration",
    description: "Smart data processing with AI algorithms for pattern recognition and trend analysis."
  },
  {
    icon: FaCloud,
    iconClass: 'text-indigo-600',
    title: "Cloud Solutions",
    description: "Scalable cloud-based scraping infrastructure for handling large-scale data collection."
  }
];

const projects = [
  {
    title: "E-commerce Price Tracker",
    description: "Automated price monitoring system for multiple e-commerce platforms with real-time alerts and historical data analysis.",
    technologies: ["Python", "Selenium", "MongoDB", "AWS"],
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Market Research Platform",
    description: "Comprehensive market research tool that collects and analyzes data from various sources to provide actionable business insights.",
    technologies: ["Node.js", "Puppeteer", "PostgreSQL", "Docker"],
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Social Media Analytics",
    description: "Real-time social media data collection and analysis platform for trend monitoring and sentiment analysis.",
    technologies: ["Python", "Scrapy", "Elasticsearch", "Kubernetes"],
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=800&q=80"
  }
];

const WebScraping: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Back Button */}
      <Link
        to="/"
        className="fixed top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 shadow-sm"
      >
        <svg 
          className="w-5 h-5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M10 19l-7-7m0 0l7-7m-7 7h18" 
          />
        </svg>
        <span className="font-medium">Portfolio</span>
      </Link>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-6xl font-bold mb-6 leading-tight pb-1 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
            >
              Web Scraping Solutions
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-300 mb-12"
            >
              Advanced data collection and analysis solutions for business intelligence and market research
            </motion.p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-800 dark:text-white">
            Our Capabilities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">
                  {feature.icon && React.createElement(feature.icon as React.ComponentType<{ className: string }>, { className: `text-4xl ${feature.iconClass}` })}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-800 dark:text-white">
            Case Studies
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* Contextual Sense Resolution Case Study */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
                  Contextual Sense Resolution Using Supervised Learning
                </h3>
                
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    This initiative explores how classification techniques can be leveraged to tackle the challenge of lexical ambiguity—determining the intended interpretation of a term that may carry multiple meanings depending on its linguistic environment.
                  </p>

                  <div className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-xl mb-8">
                    <h4 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-200">Dataset and Context</h4>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      For our analysis, we utilize a collection of English-language sentences, each annotated to reflect the contextual usage of the ambiguous term "interest." According to the Longman Dictionary, "interest" may reflect one of six semantic interpretations:
                    </p>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Category 1 – 361 instances (15%): attention or mental engagement</li>
                      <li>Category 2 – 11 instances (1%): something that provokes attention</li>
                      <li>Category 3 – 66 instances (3%): hobbies or pursuits one is engaged in</li>
                      <li>Category 4 – 178 instances (8%): benefit, advantage, or influence</li>
                      <li>Category 5 – 500 instances (21%): ownership in a company or asset</li>
                      <li>Category 6 – 1252 instances (53%): financial earnings from lending</li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 gap-6 mb-8">
                    <div className="bg-purple-50 dark:bg-purple-900/30 p-6 rounded-xl">
                      <h4 className="text-lg font-semibold mb-4 text-purple-800 dark:text-purple-200">Methodology</h4>
                      <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                        <li>🧹 Data Handling & Preprocessing</li>
                        <li>🧠 Model Training with Scikit-Learn</li>
                        <li>🛠️ Feature Engineering</li>
                        <li>🧭 Contextual Analysis</li>
                        <li>🚀 Advanced NLP Techniques</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 p-6 rounded-xl">
                      <h4 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-200">Key Findings</h4>
                      <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                        <li>Random Forest showed best performance</li>
                        <li>Larger context windows improved accuracy</li>
                        <li>Stopwords proved valuable for context</li>
                        <li>Custom feature engineering was crucial</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-center">
                    <motion.a
                      href="/report.pdf"
                      download
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      <span className="text-xl">📄</span>
                      <span>Download Full Report (PDF)</span>
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Real Estate Price Predictor Case Study */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
                  Real Estate Price Predictor
                </h3>
                
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-300">
                    This project is an end-to-end solution for automating real estate price estimation using scraped data from Denmark's leading property site, boliga.dk. It is built for scalability, precision, and real-world usability — making it an ideal backend engine for valuation apps, dashboards, or investment analysis.
                  </p>

                  <div className="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-xl">
                    <h4 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-200">What I Built</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>🕷️ Web Scraper that dynamically pulls recent sales data by street and property type</li>
                      <li> Address Extraction Engine using advanced regex to parse complex address formats</li>
                      <li>📊 Data Filtering System to clean and narrow datasets based on property area, build year, and sale date</li>
                      <li>💸 Price Prediction Algorithm that calculates estimated value using median price per m²</li>
                      <li>🧪 Unit Testing suite to ensure accurate address parsing and data integrity</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-900/30 p-6 rounded-xl">
                    <h4 className="text-lg font-semibold mb-4 text-purple-800 dark:text-purple-200">Tech Stack</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Python 3 with a fully modular architecture</li>
                      <li>BeautifulSoup4, requests → for HTML parsing and scraping</li>
                      <li>pandas → for data cleaning, transformation, and statistics</li>
                      <li>unittest → for test-driven development (regex validation)</li>
                      <li>argparse + main-dec → for clean execution logic</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/30 p-6 rounded-xl">
                    <h4 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-200">Code Structure</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>scrape_latest_sales_prices.py: Scrapes property sales data by street, zip, and property type</li>
                      <li>predict_sales_price.py: Calculates suggested selling price for a given area and filters</li>
                      <li>utils.py: Houses the core logic for filtering, analysis, and median-based prediction</li>
                      <li>test_address_regex.py: Unit tests for verifying regex performance on diverse address formats</li>
                      <li>eberts_villaby.txt: Sample street list for batch scraping</li>
                      <li>requirements.txt: Clean dependency file for environment setup</li>
                    </ul>
                  </div>

                  <div className="mt-8 flex justify-center">
                    <motion.a
                      href="/real-estate-predictor/data.csv"
                      download
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      <span className="text-xl">📊</span>
                      <span>Download Sample Data</span>
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Back to Portfolio Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 text-center">
          <motion.a
            href="/"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-white text-blue-600 font-bold py-3 px-8 rounded-full text-lg hover:shadow-lg transition-all"
          >
            ← Retour au Portfolio
          </motion.a>
        </div>
      </section>
    </div>
  );
};

export default WebScraping; 